import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import Image from "../components/common/Image";
import { useEffect, useState } from "react";
import useScreen from "../components/common/UseScreen";
import ProductForm, { FormData } from "../components/products-and-services/ProductForm";
import { ProductOrServiceId, productsAndServices } from "../data/products-and-services";
import { SnackbarMsg } from "../data/texts";
import { Icons, Images } from "../models/assets-files";
import { BlockchainAnalysisService } from "../services/product.service";
import { ImageService } from "../services/image.service";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../store/system.store";
import { auth } from "../config/firebase";
import { useLocation, useNavigate } from "react-router-dom";

const BlockchainRequestPage = () => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useScreen();
  const wrapBreakingPoint = useMediaQuery(theme.breakpoints.up(1200));
  const product = productsAndServices.find((p) => p.id === ProductOrServiceId.BLOCKCHAIN_ANALYSIS);
  if (!product) return null;
  const [clientData, setClientData] = useState<any>(null);

  const location = useLocation();

  useEffect(() => {
    // Check if we have client data from agent navigation
    if (location.state?.clientData) {
      setClientData(location.state.clientData);
    }
  }, [location.state]);

  const handleFormSubmit = async (formData: FormData, href: string) => {
    try {
      const updatedFormData = {
        ...formData,
      };
      if (clientData) {
        updatedFormData.agentClientInfo = JSON.stringify({
          crmProfileId: clientData.crmProfileId,
          clientId: clientData.id,
          clientName: clientData.name,
          clientEmail: clientData.email,
          totalAmount: clientData.totalAmount,
        });
      }
      setIsLoading(true);
      await BlockchainAnalysisService.sendBlockchainAnalysisRequest(updatedFormData, href, product?.hasFile || false);
      dispatch(showSnackbar({ msg: SnackbarMsg.FORM_SUBMIT }));
      setIsLoading(false);
      if (auth.currentUser) {
        await auth.currentUser.getIdToken(true);
      }
      // Navigate to status page after successful submission
      navigate("/blockchain-analysis/status");
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        showSnackbar({
          msg: error?.response?.data?.message || SnackbarMsg.SOMETHING_WRONG,
          isError: true,
        })
      );
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: wrapBreakingPoint ? "nowrap" : "wrap",
        gap: "50px",
        background: "white",
        padding: "24px",
        justifyContent: "space-between",
      }}
    >
      <Box
        sx={{
          paddingInlineStart: isMobile ? "0" : "20px",
          ...theme.customStyles.columnStart,
          width: wrapBreakingPoint ? "50%" : "100%",
        }}
      >
        <Box sx={{ height: "200px" }}>
          <Image
            sx={{ alignSelf: "center", height: "188px" }}
            src={ImageService.getLocalImgPath(Images.Folders.PRODUCT_PAGE, Images.ProductPage.MAGNIFY_GLASS_WOMAN)}
          />
        </Box>
        <Box
          sx={{
            ...theme.customStyles.flexStart,
            gap: "12px",
            marginBottom: "12px",
          }}
        >
          <Image sx={{ width: "40px", height: "40px" }} src={ImageService.getLocalIconPath(Icons.Folders.PRODUCTS_AND_SERVICES, product.icon)} />
          <Typography
            sx={{
              fontWeight: "400",
              fontSize: "30px",
              color: theme.palette.primary.main,
            }}
          >
            {product.title}
          </Typography>
        </Box>
        <Typography sx={{ color: theme.palette.secondary.main }}>{product.description}</Typography>
      </Box>
      <Box sx={{ width: wrapBreakingPoint ? "50%" : "100%" }}>
        <ProductForm product={product} onSubmit={handleFormSubmit} />
      </Box>
    </Box>
  );
};

export default BlockchainRequestPage;
